<script setup lang="ts">
import type { Employee } from '@/types/api';
import Button from 'primevue/button';
import { computed } from 'vue';

const props = defineProps<{
  user: Employee;
  color?: string; // hex string, e.g. "0A9954"
  size?: 'normal' | 'small';
  showName?: boolean;
  showTrash?: boolean;
  showEdit?: boolean;
  accent?: boolean; // Shows a visual accent (e.g., for time assignments)
}>();

const emit = defineEmits<{
  (e: 'trash'): void;
  (e: 'edit'): void;
}>();

// Helper: Convert hex to rgba with given alpha
function hexToRgba(hex: string, alpha: number) {
  let c = hex.replace('#', '');
  if (c.length === 3)
    c = c
      .split('')
      .map((x) => x + x)
      .join('');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// Compute background style (25% opacity)
const badgeStyle = computed(() => {
  if (!props.color) return { backgroundColor: 'rgba(170,170,170,0.25)', color: '#aaaaaa' };
  let hex = props.color;
  if (!hex.startsWith('#')) hex = '#' + hex;
  return {
    backgroundColor: hexToRgba(hex, 0.25),
    color: hex,
  };
});

const initials = computed(() => {
  const first = props.user.first_name?.[0] || '';
  const last = props.user.last_name?.slice(0, 2) || '';
  return (first + last).toUpperCase();
});

const sizeClass = computed(() => (props.size === 'small' ? 'w-5 h-5 text-[10px]' : 'w-7 h-7 text-xs'));
</script>

<template>
  <div class="flex items-center gap-2">
    <div class="relative">
      <div :style="badgeStyle" :class="['flex items-center justify-center rounded-full font-bold', sizeClass]">
        {{ initials }}
      </div>
      <!-- Accent indicator -->
      <div
        v-if="accent"
        class="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-green-500 ring-1 ring-white"
        :class="size === 'small' ? 'h-1.5 w-1.5' : 'h-2 w-2'"
        title="Has time assignment"
      ></div>
    </div>
    <div v-if="showName" class="flex items-center justify-between">
      <span :class="size === 'small' ? 'text-xs' : 'text-sm'"> {{ user.first_name }} {{ user.last_name }} </span>
      <div class="flex flex-row gap-2">
        <Button v-if="showEdit" text icon="pi pi-pencil" class="ml-1 text-primary-500" aria-label="Edit" @click="emit('edit')" />
        <Button v-if="showTrash" text icon="pi pi-trash" severity="danger" class="ml-1" aria-label="Remove" @click="emit('trash')" />
      </div>
    </div>
  </div>
</template>
